/* ═══════════════════════════════════════════════════════════════
   The Designer — the zone model behind click-to-style.

   One tiny address book for the paper's non-body zones. A BAND is what
   you click (the header strip, the footer strip, the pre/post-body
   strips); a ZONE KEY is where its tokens actually live on the forma
   (`headerOddLeft` in headerFooter, `bodyTopCenter` in special, …).
   Everything in Design mode — selection, palette targeting, drops,
   clipboard — speaks through these helpers so the storage shape stays
   in exactly one place (the same shape shared/zone-layout.ts reads).
   ═══════════════════════════════════════════════════════════════ */

import type { StandardConfig, TextStyle } from '../../shared/types';

export type BandKind = 'header' | 'footer' | 'pre' | 'post';
export type PageVariant = 'first' | 'odd' | 'even';
export type ZoneColumn = 'L' | 'C' | 'R';

/** What is selected for styling on the Design-mode paper. */
export type StyleSelection =
  | { kind: 'level'; level: number }
  | { kind: 'zone'; band: BandKind }
  | null;

const VARIANT_INFIX: Record<PageVariant, string> = {
  first: 'First',
  odd: 'Odd',
  even: 'Even',
};

const COLUMN_SUFFIX: Record<ZoneColumn, string> = { L: 'Left', C: 'Center', R: 'Right' };

export const BAND_LABEL: Record<BandKind, string> = {
  header: 'Header',
  footer: 'Footer',
  pre: 'Pre-body',
  post: 'Post-body',
};

export const COLUMN_LABEL: Record<ZoneColumn, string> = { L: 'Left', C: 'Center', R: 'Right' };

/** The storage key of one band column. Pre/post ignore the page variant. */
export function zoneKeyFor(band: BandKind, variant: PageVariant, col: ZoneColumn): string {
  if (band === 'pre') return `bodyTop${COLUMN_SUFFIX[col]}`;
  if (band === 'post') return `post${COLUMN_SUFFIX[col]}`;
  return `${band}${VARIANT_INFIX[variant]}${COLUMN_SUFFIX[col]}`;
}

/** Which store a zone key lives in on the forma. */
export function zoneSource(zoneKey: string): 'hf' | 'sp' {
  return zoneKey.startsWith('header') || zoneKey.startsWith('footer') ? 'hf' : 'sp';
}

/** Read a zone's token array (never mutates; missing zone = empty). */
export function readZone(forma: StandardConfig, zoneKey: string): string[] {
  const store =
    zoneSource(zoneKey) === 'hf'
      ? (forma.headerFooter as unknown as Record<string, string[] | undefined>)
      : (forma.special as unknown as Record<string, string[] | undefined>);
  const arr = store?.[zoneKey];
  return Array.isArray(arr) ? arr : [];
}

/** A new forma with one zone replaced. Pure — the input is untouched. */
export function writeZone(forma: StandardConfig, zoneKey: string, arr: string[]): StandardConfig {
  if (zoneSource(zoneKey) === 'hf') {
    return { ...forma, headerFooter: { ...forma.headerFooter, [zoneKey]: arr } };
  }
  return { ...forma, special: { ...forma.special, [zoneKey]: arr } };
}

/* ─── Token styles — the Format Bar's per-token font/align, keyed by zone key
       then token INDEX (D3). They live beside the tokens, so every function
       that moves, inserts, removes or replaces a token calls
       `reindexTokenStyles` to keep the two aligned. ─── */

export type TokenStyleMap = Record<string, Record<number, TextStyle>>;

/**
 * Re-key one zone's token styles after its token array changed.
 *
 *  - `fromIdx >= 0, toIdx === null`  -> the token at `fromIdx` was REMOVED:
 *    its style is dropped, later styles shift down by one.
 *  - `fromIdx >= 0, toIdx >= 0`      -> the token moved from `fromIdx` to its
 *    final position `toIdx` (indexed in the array AFTER the move): its style
 *    travels with it, the styles in between shift by one.
 *  - `fromIdx < 0, toIdx >= 0`       -> a token was INSERTED at `toIdx`: later
 *    styles shift up by one; the new slot carries no style.
 *
 * Pure. Returns `styles` itself when nothing changes; an emptied zone is
 * pruned so a forma that never styled a token stays byte-identical.
 */
export function reindexTokenStyles(
  styles: TokenStyleMap | undefined,
  zoneKey: string,
  fromIdx: number,
  toIdx: number | null
): TokenStyleMap | undefined {
  const zone = styles?.[zoneKey];
  if (!styles || !zone) return styles;
  const entries = Object.entries(zone).map(([k, v]) => [Number(k), v] as [number, TextStyle]);
  if (entries.length === 0) return styles;
  let next: Array<[number, TextStyle]>;
  if (fromIdx < 0) {
    const at = toIdx ?? 0;
    next = entries.map(([i, v]) => [i >= at ? i + 1 : i, v]);
  } else if (toIdx === null) {
    next = entries.filter(([i]) => i !== fromIdx).map(([i, v]) => [i > fromIdx ? i - 1 : i, v]);
  } else if (toIdx === fromIdx) {
    return styles;
  } else {
    next = entries.map(([i, v]) => {
      if (i === fromIdx) return [toIdx, v];
      if (fromIdx < toIdx && i > fromIdx && i <= toIdx) return [i - 1, v];
      if (fromIdx > toIdx && i >= toIdx && i < fromIdx) return [i + 1, v];
      return [i, v];
    });
  }
  const out: TokenStyleMap = { ...styles };
  if (next.length === 0) delete out[zoneKey];
  else out[zoneKey] = Object.fromEntries(next);
  return Object.keys(out).length ? out : undefined;
}

function styleStoreOf(forma: StandardConfig, zoneKey: string): TokenStyleMap | undefined {
  return zoneSource(zoneKey) === 'hf'
    ? forma.headerFooter?.tokenStyles
    : forma.special?.tokenStyles;
}

function withStyleStore(
  forma: StandardConfig,
  zoneKey: string,
  styles: TokenStyleMap | undefined
): StandardConfig {
  if (zoneSource(zoneKey) === 'hf') {
    const hf = { ...forma.headerFooter, tokenStyles: styles };
    if (!styles) delete hf.tokenStyles;
    return { ...forma, headerFooter: hf };
  }
  const sp = { ...forma.special, tokenStyles: styles };
  if (!styles) delete sp.tokenStyles;
  return { ...forma, special: sp };
}

/** One token's Format Bar style (a copy), or undefined. */
export function readTokenStyle(
  forma: StandardConfig,
  zoneKey: string,
  idx: number
): TextStyle | undefined {
  const st = styleStoreOf(forma, zoneKey)?.[zoneKey]?.[idx];
  return st ? { ...st } : undefined;
}

/** Set (or clear with `undefined`) one token's style. Empty style = cleared. */
export function writeTokenStyle(
  forma: StandardConfig,
  zoneKey: string,
  idx: number,
  style: TextStyle | undefined
): StandardConfig {
  const store = styleStoreOf(forma, zoneKey);
  const clean = style && Object.keys(style).length ? style : undefined;
  if (!clean && !store?.[zoneKey]?.[idx]) return forma;
  const zone = { ...(store?.[zoneKey] || {}) };
  if (clean) zone[idx] = clean;
  else delete zone[idx];
  const next: TokenStyleMap = { ...(store || {}) };
  if (Object.keys(zone).length) next[zoneKey] = zone;
  else delete next[zoneKey];
  return withStyleStore(forma, zoneKey, Object.keys(next).length ? next : undefined);
}

function reindexIn(
  forma: StandardConfig,
  zoneKey: string,
  fromIdx: number,
  toIdx: number | null
): StandardConfig {
  const store = styleStoreOf(forma, zoneKey);
  if (!store?.[zoneKey]) return forma;
  const next = reindexTokenStyles(store, zoneKey, fromIdx, toIdx);
  return next === store ? forma : withStyleStore(forma, zoneKey, next);
}

/** The page variants this forma actually has, in edit order. */
export function pageVariants(forma: StandardConfig | null): PageVariant[] {
  const out: PageVariant[] = [];
  if (forma?.docSettings?.firstPageDifferent) out.push('first');
  out.push('odd');
  if (forma?.docSettings?.oddEvenDifferent) out.push('even');
  return out;
}

/** Map the Design-mode page variant onto the ZoneBand's Word variant name. */
export function bandVariantName(variant: PageVariant): 'First' | 'Odd' | 'Even' {
  return VARIANT_INFIX[variant] as 'First' | 'Odd' | 'Even';
}

/* ─── Zone edits — the SAME semantics the classic forma canvas applies
       (PageCanvas's insertAtInZone / replaceAtInZone / zoneDrop), expressed
       as pure StandardConfig → StandardConfig transforms so the Designer's
       draft pipeline can undo/save them as single edits. ─── */

/** `token:<zoneKey>:<idx>` → its parts. */
export function parseTokenKey(key: string): { zoneKey: string; idx: number } {
  const r = key.slice('token:'.length);
  const li = r.lastIndexOf(':');
  return { zoneKey: r.slice(0, li), idx: parseInt(r.slice(li + 1), 10) };
}

export function zoneAppend(forma: StandardConfig, zoneKey: string, value: string): StandardConfig {
  return writeZone(forma, zoneKey, [...readZone(forma, zoneKey), value]);
}

export function zoneRemoveAt(forma: StandardConfig, zoneKey: string, idx: number): StandardConfig {
  const arr = [...readZone(forma, zoneKey)];
  if (idx < 0 || idx >= arr.length) return forma;
  arr.splice(idx, 1);
  return reindexIn(writeZone(forma, zoneKey, arr), zoneKey, idx, null);
}

export function zoneUpdateAt(
  forma: StandardConfig,
  zoneKey: string,
  idx: number,
  value: string
): StandardConfig {
  const arr = [...readZone(forma, zoneKey)];
  if (idx < 0 || idx >= arr.length) return forma;
  arr[idx] = value;
  return writeZone(forma, zoneKey, arr);
}

/** Insert at a position, optionally pulling the value out of a source zone
    first (drag-move), optionally starting a new visual line. Mirrors the
    classic canvas's insertAtInZone. */
export function zoneInsertAt(
  forma: StandardConfig,
  zoneKey: string,
  insertIdx: number,
  value: string,
  sourceZone?: string | null,
  sourceIdx?: number | null,
  newLine?: boolean
): StandardConfig {
  let next = forma;
  let idx = insertIdx;
  // A dragged token takes its Format Bar style with it.
  const carried =
    sourceZone != null && sourceIdx != null
      ? readTokenStyle(forma, sourceZone, sourceIdx)
      : undefined;
  if (sourceZone != null && sourceIdx != null) {
    next = zoneRemoveAt(next, sourceZone, sourceIdx);
    if (sourceZone === zoneKey && sourceIdx < idx) idx--;
  }
  const arr = [...readZone(next, zoneKey)];
  idx = Math.max(0, Math.min(idx, arr.length));
  // How many array slots the splice adds, and where the VALUE lands.
  let added = 1;
  let valueAt = idx;
  if (value === '\n') {
    arr.splice(idx, 0, '\n');
  } else if (newLine && idx > 0 && arr[idx - 1] !== '\n') {
    arr.splice(idx, 0, '\n', value);
    added = 2;
    valueAt = idx + 1;
  } else if (newLine && idx === 0 && arr.length > 0 && arr[0] !== '\n') {
    arr.splice(idx, 0, value, '\n');
    added = 2;
  } else {
    arr.splice(idx, 0, value);
  }
  next = writeZone(next, zoneKey, arr);
  for (let n = 0; n < added; n++) next = reindexIn(next, zoneKey, -1, idx);
  return carried && value !== '\n' ? writeTokenStyle(next, zoneKey, valueAt, carried) : next;
}

/** Replace one token, optionally consuming a dragged source token. A source
 *  in the SAME zone is removed too (the target index is corrected when the
 *  source sat before it) — a replace never leaves a duplicate, either way. */
export function zoneReplaceAt(
  forma: StandardConfig,
  zoneKey: string,
  replaceIdx: number,
  value: string,
  sourceZone?: string | null,
  sourceIdx?: number | null
): StandardConfig {
  let next = forma;
  let at = replaceIdx;
  const hasSource = sourceZone != null && sourceIdx != null;
  // Dropping a token onto itself changes nothing.
  if (hasSource && sourceZone === zoneKey && sourceIdx === replaceIdx) return forma;
  const carried = hasSource ? readTokenStyle(forma, sourceZone, sourceIdx) : undefined;
  if (hasSource) {
    next = zoneRemoveAt(next, sourceZone, sourceIdx);
    if (sourceZone === zoneKey && sourceIdx < at) at--;
  }
  // The replaced token is gone; the newcomer brings its own style (or none).
  return writeTokenStyle(zoneUpdateAt(next, zoneKey, at, value), zoneKey, at, carried);
}

/** Where a token ends up (its FINAL index) after a one-slot nudge — note that
 *  when a rightward hop empties the old line, the dead break is deleted and the
 *  token lands back at `idx`. Null when the nudge is
 *  blocked (zone end, or a hop across a line break with no line to land on).
 *  Crossing a `'\n'` is a deliberate HOP: Ctrl+Left at a line start goes to
 *  the END of the previous line, Ctrl+Right at a line end goes to the START
 *  of the next. If that leaves the old line empty, its break is dropped. */
export function zoneNudgeTargetIdx(arr: string[], idx: number, dir: -1 | 1): number | null {
  const t = idx + dir;
  if (idx < 0 || idx >= arr.length || arr[idx] === '\n' || t < 0 || t >= arr.length) return null;
  if (arr[t] !== '\n') return t;
  // Is there a real line on the far side of the break to hop onto?
  const far = dir < 0 ? arr.slice(0, t) : arr.slice(t + 1);
  if (far.every((x) => x === '\n')) return null;
  return hopEmptiesOldLine(arr, idx, dir) && dir > 0 ? idx : t;
}

/** True when hopping across a break leaves the token's OLD line empty. */
function hopEmptiesOldLine(arr: string[], idx: number, dir: -1 | 1): boolean {
  return dir < 0
    ? idx + 1 >= arr.length || arr[idx + 1] === '\n'
    : idx - 1 < 0 || arr[idx - 1] === '\n';
}

/** The plain-English reason a nudge is blocked (for the status whisper), or
 *  null when it can go. */
export function zoneNudgeBlockReason(arr: string[], idx: number, dir: -1 | 1): string | null {
  if (zoneNudgeTargetIdx(arr, idx, dir) != null) return null;
  return dir < 0 ? 'Already first in the zone' : 'Already last in the zone';
}

/** Move one token by one position within its zone — the keyboard / button
 *  path for reordering (Ctrl+ArrowLeft / Ctrl+ArrowRight, the ◀ ▶ buttons,
 *  Move left / Move right). Crosses line breaks (see `zoneNudgeTargetIdx`);
 *  a no-op (the SAME forma object back) at either end of the zone. */
export function zoneMoveAt(
  forma: StandardConfig,
  zoneKey: string,
  idx: number,
  dir: -1 | 1
): StandardConfig {
  const arr0 = readZone(forma, zoneKey);
  const arr = [...arr0];
  const to = zoneNudgeTargetIdx(arr, idx, dir);
  if (to == null) return forma;
  const target = idx + dir;
  const crossing = arr[target] === '\n';
  const [item] = arr.splice(idx, 1);
  arr.splice(target, 0, item);
  let next = reindexIn(writeZone(forma, zoneKey, arr), zoneKey, idx, target);
  // The hop emptied the old line: its (now doubled-up) break goes.
  if (crossing && hopEmptiesOldLine(arr0, idx, dir)) next = zoneRemoveAt(next, zoneKey, idx);
  return next;
}

/** Move (or copy) tokens by key into a target zone — appended, in key order. */
export function zoneMoveTokens(
  forma: StandardConfig,
  targetZoneKey: string,
  keys: string[],
  copy: boolean
): StandardConfig {
  const items = keys.filter((k) => k.startsWith('token:')).map(parseTokenKey);
  const picked = items
    .map(({ zoneKey, idx }) => ({
      value: readZone(forma, zoneKey)[idx],
      style: readTokenStyle(forma, zoneKey, idx),
    }))
    .filter(
      (p): p is { value: string; style: TextStyle | undefined } => typeof p.value === 'string'
    );
  const values = picked.map((p) => p.value);
  if (!values.length) return forma;
  let next = forma;
  if (!copy) {
    const byZone: Record<string, number[]> = {};
    for (const { zoneKey, idx } of items) (byZone[zoneKey] ||= []).push(idx);
    for (const [zk, idxs] of Object.entries(byZone)) {
      const arr = [...readZone(next, zk)];
      idxs
        .sort((a, b) => b - a)
        .forEach((i) => {
          if (i >= 0 && i < arr.length) {
            arr.splice(i, 1);
            next = reindexIn(next, zk, i, null);
          }
        });
      next = writeZone(next, zk, arr);
    }
  }
  const base = readZone(next, targetZoneKey).length;
  next = writeZone(next, targetZoneKey, [...readZone(next, targetZoneKey), ...values]);
  // Appended tokens keep the style they had (move or copy).
  picked.forEach((p, j) => {
    if (p.style) next = writeTokenStyle(next, targetZoneKey, base + j, p.style);
  });
  return next;
}

/** Move (or copy) tokens by key so they land AT a slot — `dstIdx` in the
 *  target zone's array BEFORE the move — in key order. Sources removed from
 *  the same zone ahead of the slot correct the index. `newLine` starts a
 *  fresh line the way `zoneInsertAt` does. Each token keeps its modifiers
 *  (they live in the string) and its Format Bar style. `zoneMoveTokens`
 *  stays for the empty-column append. */
export function zoneMoveTokensAt(
  forma: StandardConfig,
  keys: string[],
  dstZone: string,
  dstIdx: number,
  copy: boolean,
  newLine: boolean
): StandardConfig {
  const items = keys.filter((k) => k.startsWith('token:')).map(parseTokenKey);
  const picked = items
    .map(({ zoneKey, idx }) => ({
      value: readZone(forma, zoneKey)[idx],
      style: readTokenStyle(forma, zoneKey, idx),
    }))
    .filter(
      (p): p is { value: string; style: TextStyle | undefined } => typeof p.value === 'string'
    );
  if (!picked.length) return forma;
  let next = forma;
  let at = dstIdx;
  if (!copy) {
    const byZone: Record<string, number[]> = {};
    for (const { zoneKey, idx } of items) (byZone[zoneKey] ||= []).push(idx);
    for (const [zk, idxs] of Object.entries(byZone)) {
      const uniq = [...new Set(idxs)].sort((a, b) => b - a);
      for (const i of uniq) {
        const arr = readZone(next, zk);
        if (i < 0 || i >= arr.length) continue;
        next = zoneRemoveAt(next, zk, i);
        if (zk === dstZone && i < dstIdx) at--;
      }
    }
  }
  const arr = [...readZone(next, dstZone)];
  at = Math.max(0, Math.min(at, arr.length));
  const values = picked.map((p) => p.value);
  let valueAt = at;
  if (newLine && at > 0 && arr[at - 1] !== '\n') {
    arr.splice(at, 0, '\n', ...values);
    valueAt = at + 1;
  } else if (newLine && at === 0 && arr.length > 0 && arr[0] !== '\n') {
    arr.splice(at, 0, ...values, '\n');
  } else {
    arr.splice(at, 0, ...values);
  }
  const added = arr.length - readZone(next, dstZone).length;
  next = writeZone(next, dstZone, arr);
  for (let n = 0; n < added; n++) next = reindexIn(next, dstZone, -1, at);
  picked.forEach((p, j) => {
    if (p.style && p.value !== '\n') next = writeTokenStyle(next, dstZone, valueAt + j, p.style);
  });
  return next;
}

/** Apply a value-transform to every given token key (multi-select format). */
export function zoneApplyToKeys(
  forma: StandardConfig,
  keys: string[],
  fn: (value: string) => string
): StandardConfig {
  const byZone: Record<string, number[]> = {};
  for (const k of keys) {
    if (!k.startsWith('token:')) continue;
    const { zoneKey, idx } = parseTokenKey(k);
    (byZone[zoneKey] ||= []).push(idx);
  }
  let next = forma;
  for (const [zk, idxs] of Object.entries(byZone)) {
    const arr = [...readZone(next, zk)];
    let touched = false;
    for (const i of idxs) {
      // A {Line} rule is not text: bold/size/colour/case must not touch it.
      if (i >= 0 && i < arr.length && typeof arr[i] === 'string' && !arr[i].startsWith('{Line')) {
        arr[i] = fn(arr[i]);
        touched = true;
      }
    }
    if (touched) next = writeZone(next, zk, arr);
  }
  return next;
}

/** Delete every given token key. */
export function zoneDeleteKeys(forma: StandardConfig, keys: string[]): StandardConfig {
  const byZone: Record<string, number[]> = {};
  for (const k of keys) {
    if (!k.startsWith('token:')) continue;
    const { zoneKey, idx } = parseTokenKey(k);
    (byZone[zoneKey] ||= []).push(idx);
  }
  let next = forma;
  for (const [zk, idxs] of Object.entries(byZone)) {
    const arr = [...readZone(next, zk)];
    idxs
      .sort((a, b) => b - a)
      .forEach((i) => {
        if (i >= 0 && i < arr.length) {
          arr.splice(i, 1);
          next = reindexIn(next, zk, i, null);
        }
      });
    next = writeZone(next, zk, arr);
  }
  return next;
}
